import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { showToast } from '../components/Toast';
import './Dashboard.css';

const Dashboard = () => {
  const [recorridos, setRecorridos] = useState([]);
  const [recorridosFiltrados, setRecorridosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtroPublico, setFiltroPublico] = useState('todos');

  useEffect(() => {
    cargarRecorridos();
  }, []);

  useEffect(() => {
    filtrarRecorridos();
  }, [busqueda, filtroPublico, recorridos]);

  const cargarRecorridos = async () => {
    try {
      const response = await api.get('/recorridos');
      if (response.data.success) {
        setRecorridos(response.data.recorridos);
        if (response.data.recorridos.length === 0) {
          showToast('No tienes recorridos aún. ¡Crea tu primer recorrido!', 'info', 5000);
        }
      }
    } catch (err) {
      setError('Error al cargar recorridos');
      showToast('Error al cargar recorridos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filtrarRecorridos = () => {
    let filtrados = [...recorridos];

    // Filtrar por búsqueda
    if (busqueda) {
      filtrados = filtrados.filter(recorrido =>
        recorrido.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
        (recorrido.descripcion && recorrido.descripcion.toLowerCase().includes(busqueda.toLowerCase()))
      );
    }

    // Filtrar por público/privado
    if (filtroPublico === 'publico') {
      filtrados = filtrados.filter(recorrido => recorrido.publico);
    } else if (filtroPublico === 'privado') {
      filtrados = filtrados.filter(recorrido => !recorrido.publico);
    }

    setRecorridosFiltrados(filtrados);
  };

  const eliminarRecorrido = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este recorrido?')) {
      return;
    }

    try {
      await api.delete(`/recorridos/${id}`);
      cargarRecorridos();
    } catch (err) {
      alert('Error al eliminar recorrido');
    }
  };

  const estadisticas = {
    total: recorridos.length,
    publicos: recorridos.filter(r => r.publico).length,
    privados: recorridos.filter(r => !r.publico).length,
    totalImagenes: recorridos.reduce((sum, r) => sum + (r.total_imagenes || 0), 0)
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>Mi Dashboard</h1>
            <p className="dashboard-subtitle">Gestiona tus recorridos virtuales 360º</p>
          </div>
          <Link to="/crear-recorrido" className="btn btn-primary">
            + Nuevo Recorrido
          </Link>
        </div>

        {/* Estadísticas */}
        {recorridos.length > 0 && (
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-value">{estadisticas.total}</div>
              <div className="stat-label">Total Recorridos</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{estadisticas.publicos}</div>
              <div className="stat-label">Públicos</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{estadisticas.privados}</div>
              <div className="stat-label">Privados</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{estadisticas.totalImagenes}</div>
              <div className="stat-label">Total Imágenes</div>
            </div>
          </div>
        )}

        {/* Búsqueda y Filtros */}
        {recorridos.length > 0 && (
          <div className="dashboard-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Buscar recorridos..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filtroPublico === 'todos' ? 'active' : ''}`}
                onClick={() => setFiltroPublico('todos')}
              >
                Todos
              </button>
              <button
                className={`filter-btn ${filtroPublico === 'publico' ? 'active' : ''}`}
                onClick={() => setFiltroPublico('publico')}
              >
                Públicos
              </button>
              <button
                className={`filter-btn ${filtroPublico === 'privado' ? 'active' : ''}`}
                onClick={() => setFiltroPublico('privado')}
              >
                Privados
              </button>
            </div>
          </div>
        )}

        {error && <div className="error">{error}</div>}

        {recorridos.length === 0 ? (
          <div className="empty-state">
            <h2>No tienes recorridos aún</h2>
            <p>Crea tu primer recorrido virtual 360º</p>
            <Link to="/crear-recorrido" className="btn btn-primary">
              Crear Recorrido
            </Link>
          </div>
        ) : recorridosFiltrados.length === 0 ? (
          <div className="empty-state">
            <h2>No se encontraron recorridos</h2>
            <p>Intenta ajustar tus filtros de búsqueda</p>
          </div>
        ) : (
          <div className="grid">
            {recorridosFiltrados.map((recorrido) => {
              const apiUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
              const imagenUrl = recorrido.primera_imagen ? `${apiUrl}${recorrido.primera_imagen}` : null;
              
              console.log('🖼️ [Dashboard] Recorrido:', {
                id: recorrido.id,
                titulo: recorrido.titulo,
                primera_imagen: recorrido.primera_imagen,
                imagenUrl: imagenUrl,
                total_imagenes: recorrido.total_imagenes
              });
              
              return (
              <div key={recorrido.id} className="recorrido-card">
                {recorrido.primera_imagen ? (
                  <div className="recorrido-imagen">
                    <img 
                      src={imagenUrl}
                      alt={recorrido.titulo}
                      onError={(e) => {
                        console.error('❌ Error cargando imagen:', imagenUrl);
                        e.target.src = 'https://via.placeholder.com/400x300?text=Sin+Imagen';
                      }}
                      onLoad={() => {
                        console.log('✅ Imagen cargada correctamente:', imagenUrl);
                      }}
                    />
                  </div>
                ) : (
                  <div className="recorrido-imagen" style={{background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <span style={{color: '#999'}}>Sin imagen</span>
                  </div>
                )}
                <div className="recorrido-content">
                  <h3>{recorrido.titulo}</h3>
                  <p className="recorrido-descripcion">
                    {recorrido.descripcion || 'Sin descripción'}
                  </p>
                  <div className="recorrido-stats">
                    <span>{recorrido.total_imagenes || 0} imágenes</span>
                    <span className={recorrido.publico ? 'publico' : 'privado'}>
                      {recorrido.publico ? 'Público' : 'Privado'}
                    </span>
                  </div>
                  <div className="recorrido-actions">
                    <Link 
                      to={`/editar-recorrido/${recorrido.id}`} 
                      className="btn btn-primary btn-sm"
                    >
                      Editar
                    </Link>
                    <Link 
                      to={`/visor/id/${recorrido.id}`} 
                      className="btn btn-success btn-sm"
                      onClick={() => {
                        // Asegurar que el token esté disponible
                        const token = localStorage.getItem('token');
                        if (!token) {
                          alert('Por favor, inicia sesión para ver el recorrido');
                          return false;
                        }
                      }}
                    >
                      Ver
                    </Link>
                    <button 
                      onClick={() => eliminarRecorrido(recorrido.id)}
                      className="btn btn-danger btn-sm"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

